<template>
    <div class="ph-challenges-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="ph-challenges-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="ph-challenges-table__top-wrapper">
                    <KAutocomplete
                        id="select-challenge-list"
                        v-model="selectedChallengeId"
                        name="challengeList"
                        :get-items="challengesItems"
                        item-title="name"
                        item-value="id"
                        label=""
                        :placeholder="$t('salesRep.selectChallenge')"
                        variant="outlined"
                        hide-validation
                    />
                </div>
            </template>

            <template #item.record_id="elem">
                <div v-if="elem.item.id != null">{{ elem.item.id }}</div>
            </template>

            <template #item.created_at="elem">
                <div v-if="elem.item.created_at != null">{{ useDataFormat(elem.item.created_at) }}</div>
            </template>

            <template #item.name="elem">
                <div v-if="elem.item.name != null">{{ elem.item.name }}</div>
            </template>

            <template #item.description="elem">
                <div v-if="elem.item.description != null">{{ elem.item.description }}</div>
            </template>

            <template #bottom>

            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import ChallengeApi from '@/api/v1/ChallengeApi';
import { toast } from 'vue3-toastify';
import { useDataFormat } from '@/hooks/DataHooks';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IChallengesItemList, IChallengesItemProductList } from '@/models/ChallengesModel';
import KAutocomplete from '@/components/atoms/KAutocomplete.vue';

const headers = ref<ITableHeader[]>([
    {
        title: t('salesRep.idRecord'),
        align: 'start',
        sortable: false,
        key: 'record_id',
        width: '',
        minWidth: '120px',
    },
    {
        title: t('salesRep.data'),
        align: 'center',
        sortable: false,
        key: 'created_at',
        width: '',
        minWidth: '130px',
    },
    {
        title: t('salesRep.nameHeader'),
        align: 'center',
        sortable: false,
        key: 'name',
        width: '',
        minWidth: '',
    },
    {
        title: t('salesRep.descriptionHeader'),
        align: 'center',
        sortable: false,
        key: 'description',
        width: '',
        minWidth: '',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IChallengesItemProductList[]>([]);
const challengesItems = ref<IChallengesItemList[]>([]);
const selectedChallengeId = ref<number | null>(null);
const challengeApi = new ChallengeApi();
const challengeList = async() => {
    try {
        const result = await challengeApi.challengeList(
            1,
            1000,
            null,
            null,
        );
        if (!result.isSuccess) {
            toast.error(t('challenges.toast.errorChallengesList'));
            return;
        }
        challengesItems.value = result.payload.data;
    } catch (e) {
        toast.error(t('challenges.toast.errorChallengesList'));
        return;
    }
};
challengeList();
const oneChallengeList = async() => {
    if (selectedChallengeId.value == null) {
        toast.error(t('salesRep.validation.errorSelectedChallengeId'));
        return;
    }
    loading.value = true;
    try {
        const result = await challengeApi.oneChallengeList(selectedChallengeId.value);
        if (!result.isSuccess) {
            toast.error(t('salesRep.toast.errorChallengeList'));
            loading.value = false;
            return;
        }
        items.value = result.payload.products;
    } catch (e) {
        toast.error(t('salesRep.toast.errorChallengeList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
};
watch(() => selectedChallengeId.value, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        oneChallengeList();
    }
},{
    deep: true,
});
</script>

<style scoped lang="scss">
.ph-challenges-table {
    &__loading {
        opacity: 0.6;
    }

    &__top-wrapper {
        padding: 10px;
        background-color: $primary-800;

        &:deep(.k-autocomplete) {
            background-color: $white;
            border-radius: 5px;
        }
    }
}
</style>
